import React, { useContext, useState, useEffect } from 'react';
import {
    AppBar,
    IconButton,
    Typography,
    Avatar,
    Stack,
    Breadcrumbs,
    Link,
    Box,
    Tooltip,
    Toolbar,
    Divider
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import packageJson from '../../../package.json'
import logo from '../../assets/AltopLogoIcon.png'
import { logout, redirectToLogin, getUserName, LOGIN_URL, apiFetch } from '../../utils/auth';
import { ColorModeContext } from './ThemeContext';
import { Logout, Home2 as HomeIcon } from 'iconsax-react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import MiniDashboardDrawer from './MiniDashboardDrawer';

const TopHeader = ({
    appName,
    subCategory,
}) => {
    const theme = useTheme();
    const { toggleColorMode, mode } = useContext(ColorModeContext);
    const username = getUserName();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const userInitials = typeof username === "string"
        ? username.slice(0, 2).toUpperCase()
        : "";
    const [apps, setApps] = useState([]);
    const [favorites, setFavorites] = useState([]);
    const [expandedApps, setExpandedApps] = useState({});

    const handleLogout = () => {
        logout();
        redirectToLogin();
    };
    const groupAppsByAppname = (appsArray) => {
        return appsArray.reduce((acc, app) => {
            if (!acc[app.appname]) acc[app.appname] = [];
            acc[app.appname].push(app);
            return acc;
        }, {});
    };

    useEffect(() => {
        const fetchApps = async () => {
            try {
                apiFetch("/login/apps")
                    .then((response) => {
                        if (!response.ok) {
                            throw new Error("Network response was not ok");
                        }
                        return response.json();
                    })
                    .then((response) => {
                        setApps(response.apps);
                        setFavorites(response.faves || []);
                        createFrequentAppnameObject(response.apps);
                    })
            } catch (error) {
                console.error("Failed to fetch apps:", error);
                setApps([]);
            }
        };
        fetchApps();
    }, []);

    function createFrequentAppnameObject(jsonData) {
        const counts = {};
        const result = {};

        jsonData.forEach(app => {
            if (app.appname) {
                counts[app.appname] = (counts[app.appname] || 0) + 1;
            }
        });

        for (const appname in counts) {
            if (counts[appname] > 1) {
                result[appname] = false;
            }
        }

        setExpandedApps(result);
    };

    return (
        <AppBar elevation={0} position='sticky'
            sx={{
                bgcolor: theme.palette.customColors.mediumBackground,
                backgroundColor: theme.palette.customColors.mediumBackground,
                padding: '1px',
                border: theme.palette.customColors.border,
                color: theme.palette.text.primary
            }}
        >
            <Toolbar disableGutters variant="dense" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} >
                <MiniDashboardDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} apps={apps} favorites={favorites} expandedApps={expandedApps} setExpandedApps={setExpandedApps} groupAppsByAppname={groupAppsByAppname} />
                <Stack direction="row" alignItems="center" spacing={2} sx={{ px: 1 }}>
                    <Box
                        onClick={() => setDrawerOpen(true)}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 30,
                            height: 30,
                            borderRadius: '8px',
                            cursor: 'pointer',
                            background: theme.palette.mode === 'dark' ? 'rgba(225,228,232,0.85)' : 'transparent',
                            border: theme.palette.mode === 'dark' ? `1px solid rgba(255,255,255,0.15)` : 'none',
                            transition: 'transform 120ms ease',
                            '&:hover': { transform: 'scale(1.05)' },
                        }}
                    >
                        <img src={logo} alt="Custom Logo" width="22" height="22" />
                    </Box>

                    <Typography noWrap color={theme.palette.text.primary} sx={{ fontSize: '15px' }}>
                        {appName}
                    </Typography>
                    <Divider orientation="vertical" variant="middle" flexItem />
                    <Breadcrumbs separator={<ChevronRightIcon fontSize="small" />} sx={{ color: theme.palette.text.primary, '& .MuiBreadcrumbs-separator': { m: 0 } }}>
                        <Link
                            color="inherit"
                            component="button"
                            onClick={() => window.location.assign(`${LOGIN_URL}`)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                textDecoration: 'none',
                                color: theme.palette.text.primary // Ensure the link text color
                            }}
                        >
                            <HomeIcon variant="Bold" size="16" color={theme.palette.customColors.altopColor} />
                        </Link>
                        <Typography sx={{ fontSize: '14px' }}>{subCategory}</Typography>
                    </Breadcrumbs>
                </Stack>

                {/* Right side: theme toggle, user chip with version, logout */}
                <Stack direction="row" alignItems="center" spacing={1} sx={{ pr: 1 }}>
                    <Tooltip title={`Switch to ${mode === 'dark' ? 'light' : 'dark'} mode`}>
                        <IconButton
                            onClick={toggleColorMode}
                            size="small"
                            sx={{
                                width: 28,
                                height: 28,
                                borderRadius: '8px',
                                color: theme.palette.text.secondary,
                                '&:hover': { color: theme.palette.text.primary, background: theme.palette.action.hover },
                            }}
                        >
                            {mode === 'dark' ? <LightModeIcon sx={{ fontSize: 16 }} /> : <DarkModeIcon sx={{ fontSize: 16 }} />}
                        </IconButton>
                    </Tooltip>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 0.75,
                            py: 0.25,
                            my: 0.5,
                            borderRadius: '10px',
                            border: `1px solid ${theme.palette.divider}`,
                            background: theme.palette.mode === 'dark'
                                ? 'rgba(255,255,255,0.04)'
                                : 'rgba(0,72,229,0.04)',
                        }}
                    >
                        <Avatar
                            sx={{
                                background: theme.palette.customColors.mainColor,
                                color: '#fff',
                                width: 24,
                                height: 24,
                                fontSize: 10.5,
                                fontWeight: 700,
                                letterSpacing: '0.04em',
                                flexShrink: 0,
                            }}
                        >
                            {userInitials}
                        </Avatar>
                        <Typography
                            noWrap
                            sx={{
                                fontSize: '13px',
                                lineHeight: 1.1,
                                color: theme.palette.text.primary,
                            }}
                        >
                            {username}
                        </Typography>
                        <Box
                            component="span"
                            sx={{
                                flexShrink: 0,
                                fontSize: '10.5px',
                                fontWeight: 600,
                                fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                                letterSpacing: '0.04em',
                                color: theme.palette.text.secondary,
                                bgcolor: theme.palette.mode === 'dark'
                                    ? 'rgba(255,255,255,0.08)'
                                    : 'rgba(0,0,0,0.04)',
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: '4px',
                                px: 0.5,
                                py: '1px',
                                lineHeight: 1.2,
                            }}
                        >
                            v{packageJson.version}
                        </Box>
                    </Box>

                    <Tooltip title="Sign out" placement="bottom">
                        <IconButton
                            size="small"
                            onClick={handleLogout}
                            sx={{
                                width: 28,
                                height: 28,
                                borderRadius: '8px',
                                color: theme.palette.text.secondary,
                                transition: 'all 120ms ease',
                                '&:hover': {
                                    color: theme.palette.error.main,
                                    background: theme.palette.action.hover,
                                },
                            }}
                        >
                            <Logout size={14} variant="Bold" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Toolbar>
        </AppBar>
    );
};

export default TopHeader;
